/**
 * Navigation & Background & Mobile Menu - Moon Moon Agency
 * Handles navigation, background filters, and mobile menu interactions
 */

/*********************************************************************************************
 * 🎯 NAVIGATION MODULE
 */
const NavigationManager = {
	// Local configuration
	config: {
		selectors: {
			header: 'header',
			megaMenuLink: '[data-megamenulink]',
			menuLink: '[data-menulink]',
			filter: '[data-filter]',
			cartFilter: '[data-drawer-cart]',
			searchContainer: '[data-search-container]',
			headerBurger: '[data-header-burger]',
			menuMobile: '[data-menu-mobile]',
			menuSublinks: '[data-menu-sublinks]'
		},
		classes: {
			active: 'active',
			open: 'mm-open',
			drawerOpen: 'mm-drawer-open',
			stopScrolling: 'stop-scrolling',
			mobileMenuSlided: 'mm-mobile-menu-elem-slided'
		},
		timing: {
			filterHideDelay: 300
		}
	},

	/**
	 * Initialize navigation systems
	 */
	init() {
		this.initMegaMenus();
		this.initMegaMenuHover();
		this.initBackgroundFilter();
		this.initAccessibilityHandlers();
		this.initMegaMenuAccessibility();
		this.initMobileSubmenuAccessibility();
		this.initScrollState();
		this.initHeaderGroupHeight();
		this.initMobileMenuScrollHint();
	},

	/**
	 * Menu mobile V3 : le degrade « scroller » ([data-menu-mobile-scroll-hint]) disparait
	 * quand la liste est en bas (classe mm-scroll-end). Recalcule au scroll, a l'ouverture
	 * du menu et a l'ouverture/fermeture d'un <details> (la hauteur du contenu change).
	 */
	initMobileMenuScrollHint() {
		const selector = '[data-menu-mobile-scroll-hint]';
		const update = (container) => {
			const atEnd = container.scrollTop + container.clientHeight >= container.scrollHeight - 2;
			container.classList.toggle('mm-scroll-end', atEnd);
		};

		// scroll et toggle ne remontent pas : ecoute en phase de capture
		document.addEventListener('scroll', (e) => {
			if (e.target instanceof Element && e.target.matches(selector)) update(e.target);
		}, { capture: true, passive: true });
		document.addEventListener('toggle', (e) => {
			const container = e.target instanceof Element ? e.target.closest(selector) : null;
			if (container) update(container);
		}, true);
		document.addEventListener('global:mobile-menu-toggled', () => {
			window.getElements(selector).forEach(update);
		});
	},

	/**
	 * Pop-in mega menu — fermeture quand la souris sort du panneau vers
	 * l'exterieur du header (pas vers un autre lien du nav).
	 * L'ouverture/toggle est gere par initMegaMenuAccessibility (click + keyboard).
	 */
	initMegaMenuHover() {
		const header = window.getElement(this.config.selectors.header);
		if (!header) return;

		window.getElements(this.config.selectors.megaMenuLink).forEach((link) => {
			const wrapper = link.nextElementSibling;
			if (!wrapper?.hasAttribute('data-megamenu')) return;
			const panel = wrapper.querySelector('.mm-mega-menu-panel');
			if (!panel) return;

			panel.addEventListener('mouseleave', (e) => {
				/* Si on entre dans le header (autre lien du nav), garder ouvert */
				if (e.relatedTarget && header.contains(e.relatedTarget)) return;
				this.closeMegaMenu(link, wrapper);
			});
		});
	},

	/**
	 * Mesure la hauteur réelle du header-group (announcement bar + header) et
	 * l'expose via `--mm-header-group-height`. Nécessaire car le Customizer
	 * Shopify ajoute des wrappers autour des sections qui altèrent la hauteur.
	 */
	initHeaderGroupHeight() {
		const isDesignMode = !!(window.Shopify && window.Shopify.designMode);

		const update = () => {
			const sections = document.querySelectorAll('.shopify-section-group-header-group');
			if (!sections.length) return;
			let visibleBottom = 0;
			sections.forEach((s) => {
				const rect = s.getBoundingClientRect();
				/* Garde la portion encore visible (sous le viewport top) */
				const bottom = Math.max(0, rect.bottom);
				if (bottom > visibleBottom) visibleBottom = bottom;
			});
			document.documentElement.style.setProperty('--mm-header-group-height', `${Math.round(visibleBottom)}px`);
		};

		update();
		window.addEventListener('resize', window.debounce(update, 100), { passive: true });
		window.addEventListener('scroll', update, { passive: true });
		if (isDesignMode) {
			document.addEventListener('shopify:section:load', update);
			document.addEventListener('shopify:section:unload', update);
			document.addEventListener('shopify:section:reorder', update);
			document.addEventListener('shopify:section:select', update);
			document.addEventListener('shopify:section:deselect', update);
		}
	},

	/**
	 * Toggle .mm-section-header--scrolled when page is scrolled past the header
	 * Drives the past-scroll style (border + opaque bg) on transparent headers.
	 */
	initScrollState() {
		const sectionHeader = document.querySelector('.mm-section-header');
		if (!sectionHeader) return;

		const scrolledClass = 'mm-section-header--scrolled';
		const rootStyles = getComputedStyle(document.documentElement);

		const getThreshold = () => {
			const varName = window.isMobile() ? '--announcement-bar-height-mobile' : '--announcement-bar-height';
			return parseInt(rootStyles.getPropertyValue(varName), 10) || 0;
		};

		const update = () => {
			const isScrolled = window.scrollY > getThreshold();
			sectionHeader.classList.toggle(scrolledClass, isScrolled);
		};

		update();
		window.addEventListener('scroll', window.debounce(update, 10), { passive: true });
		window.log('info', 'NavigationManager', 'Scroll state initialized');
	},

	/**
	 * Initialize accessibility keyboard handlers for header buttons
	 */
	initAccessibilityHandlers() {
		// Burger menu button
		const burgerBtn = window.getElement('[data-header-burger]');
		if (burgerBtn) {
			burgerBtn.addEventListener('click', () => {
				window.mmMobileMenu();
				// aria-expanded géré dans MobileMenuManager.toggle()
			});
		}

		// Search toggle button
		const searchBtn = window.getElement('[data-search-toggle]');
		if (searchBtn) {
			searchBtn.addEventListener('click', () => {
				window.mmCloseMobileMenu();
				window.mmShowSearch(searchBtn);
				// Update aria-expanded
				const isOpen = searchBtn.getAttribute('aria-expanded') === 'true';
				searchBtn.setAttribute('aria-expanded', !isOpen);
			});
		}

		// Cart toggle button
		const cartBtn = window.getElement('[data-cart-toggle]');
		if (cartBtn) {
			cartBtn.addEventListener('click', () => {
				window.mmOpenCart(cartBtn);
				window.mmCloseMobileMenu(true);
				window.mmHideSearch(true);
			});
		}

		// Global Escape key handler (close all overlays)
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				const mobileMenu = this.getMobileMenu();
				if (mobileMenu?.classList.contains(this.config.classes.open)) {
					window.mmCloseMobileMenu();
					burgerBtn?.focus(); // Return focus to burger button
				}
			}
		});

		window.log('info', 'NavigationManager', 'Accessibility handlers initialized');
	},

	/**
	 * Initialize mega menu and submenu positioning
	 */
	initMegaMenus() {
		const header = window.getElement(this.config.selectors.header);
		if (!header) {
			window.log('warn', 'NavigationManager', 'Header not found');
			return;
		}

		const headerHeight = header.offsetHeight;

		/* Sous-menus dropdown (data-menulink) — positionnes sous le lien */
		window.getElements(this.config.selectors.menuLink).forEach((menuElem) => {
			const submenu = menuElem.querySelector('[data-megamenu]');
			if (submenu) {
				submenu.style.top = `${headerHeight - 1}px`;
			}
		});
	},

	/**
	 * Initialize mega menu keyboard accessibility
	 */
	initMegaMenuAccessibility() {
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const regularMenuLinks = window.getElements(this.config.selectors.menuLink);

		// Mega menu links (with dropdown)
		megaMenuLinks.forEach((link) => {
			const megaMenu = link.nextElementSibling;
			if (!megaMenu?.hasAttribute('data-megamenu')) return;

			// Click to toggle mega menu
			link.addEventListener('click', (e) => {
				e.preventDefault();
				this.toggleMegaMenu(link, megaMenu);
			});

			// Keyboard: Enter to toggle, Arrow Down to focus first item
			link.addEventListener('keydown', (e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					this.toggleMegaMenu(link, megaMenu);
				} else if (e.key === 'ArrowDown') {
					e.preventDefault();
					const isOpen = link.getAttribute('aria-expanded') === 'true';
					if (!isOpen) {
						this.openMegaMenu(link, megaMenu);
					}
					// Focus first link in mega menu
					const firstLink = megaMenu.querySelector('a');
					firstLink?.focus();
				}
			});

			// Sortie au Tab : fermer quand le focus quitte le bouton et son panneau (A11Y-11)
			const closeOnFocusOut = (e) => {
				const next = e.relatedTarget;
				if (!next || link.contains(next) || megaMenu.contains(next)) return;
				if (link.getAttribute('aria-expanded') === 'true') this.closeMegaMenu(link, megaMenu);
			};
			link.addEventListener('focusout', closeOnFocusOut);
			megaMenu.addEventListener('focusout', closeOnFocusOut);

		});

		// Escape key closes all mega menus
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				const openLink = Array.from(megaMenuLinks).find(
					(link) => link.getAttribute('aria-expanded') === 'true'
				);
				if (openLink) {
					// Focus dans le sous-menu : le rendre au bouton, sinon il reste sur un lien masque (A11Y-11)
					const focusInMenu = openLink.nextElementSibling?.contains(document.activeElement);
					this.closeAllMegaMenus();
					if (focusInMenu) openLink.focus();
				}
			}
		});

		// Initialize mega menu product links hover/focus
		this.initMegaMenuProductLinks();

		window.log('info', 'NavigationManager', 'Mega menu accessibility initialized');
	},

	/**
	 * Initialize mega menu product links (replace inline onmouseenter)
	 */
	initMegaMenuProductLinks() {
		const productLinks = window.getElements('[data-mega-menu-products-link]');

		productLinks.forEach((link) => {
			const hideSelector = link.dataset.hideSelector;
			const showSelector = link.dataset.showSelector;

			// Hover event
			link.addEventListener('mouseenter', () => {
				this.showMegaMenuProducts(hideSelector, showSelector);
			});

			// Focus event (keyboard navigation)
			link.addEventListener('focus', () => {
				this.showMegaMenuProducts(hideSelector, showSelector);
			});
		});
	},

	/**
	 * Toggle mega menu state
	 */
	toggleMegaMenu(link, megaMenu) {
		const isOpen = link.getAttribute('aria-expanded') === 'true';
		if (isOpen) {
			this.closeMegaMenu(link, megaMenu);
		} else {
			this.closeAllMegaMenus(); // Close others first
			this.openMegaMenu(link, megaMenu);
		}
	},

	/**
	 * Open mega menu
	 */
	openMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'true');
		megaMenu.classList.add('is-open');
		megaMenu.setAttribute('aria-hidden', 'false');
		document.documentElement.classList.add(this.config.classes.stopScrolling);
	},

	/**
	 * Close mega menu
	 */
	closeMegaMenu(link, megaMenu) {
		link.setAttribute('aria-expanded', 'false');
		megaMenu.classList.remove('is-open');
		megaMenu.setAttribute('aria-hidden', 'true');
		document.documentElement.classList.remove(this.config.classes.stopScrolling);
	},

	/**
	 * Close all mega menus
	 */
	closeAllMegaMenus() {
		const allMegaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		allMegaMenuLinks.forEach((link) => {
			link.setAttribute('aria-expanded', 'false');
			const megaMenu = link.nextElementSibling;
			if (megaMenu?.hasAttribute('data-megamenu')) {
				megaMenu.classList.remove('is-open');
				megaMenu.setAttribute('aria-hidden', 'true');
			}
		});
		document.documentElement.classList.remove(this.config.classes.stopScrolling);
	},

	/**
	 * Show specific mega menu products
	 */
	showMegaMenuProducts(classElemsToHide, classElemToShow) {
		try {
			window.getElements(classElemsToHide).forEach((elem) => (elem.style.display = 'none'));
			const elemToShow = window.getElement(classElemToShow);
			if (elemToShow) {
				elemToShow.style.display = 'grid';
			}
			window.emit('global:mega-menu-products-switched', { hideSelector: classElemsToHide, showSelector: classElemToShow });
		} catch (error) {
			window.log('error', 'NavigationManager', 'Failed to switch mega menu products', { error: error.message });
		}
	},

	/**
	 * Initialize background filter hover behaviors
	 */
	initBackgroundFilter() {
		const elements = {
			header: window.getElement(this.config.selectors.header),
			searchContainer: window.getElement(this.config.selectors.searchContainer),
			filter: window.getElement(this.config.selectors.filter),
			cart: window.getElement(this.config.selectors.cartFilter)
		};

		if (!elements.header || !elements.filter || !elements.cart) {
			window.log('warn', 'NavigationManager', 'Background filter elements not found');
			return;
		}

		const { header, searchContainer, filter, cart } = elements;
		const megaMenuLinks = window.getElements(this.config.selectors.megaMenuLink);
		const menuLinks = window.getElements(this.config.selectors.menuLink);

		const toggleFilter = (show) => {
			filter.classList.toggle(this.config.classes.active, show);
			window.emit(`global:background-filter-${show ? 'activated' : 'deactivated'}`, { 
				trigger: show ? 'toggle-on' : 'toggle-off',
				source: 'navigation' 
			});
		};

		/* Liens nav classiques (sans pop-in) : hover ferme le filter global */
		menuLinks.forEach((link) =>
			link.addEventListener('mouseenter', () => toggleFilter(false))
		);

		header.addEventListener('mouseleave', () => {
			const searchOpen = searchContainer?.classList.contains(this.config.classes.open);
			const cartOpen = cart.classList.contains(this.config.classes.drawerOpen);

			if (!searchOpen && !cartOpen) {
				toggleFilter(false);
			}
		});

	},

	/**
	 * Show background filter
	 */
	showBackgroundFilter(zIndex) {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) {
			window.log('warn', 'NavigationManager', 'Background filter element not found');
			return;
		}
		
		filter.classList.add(this.config.classes.active);
		if (zIndex) filter.style.zIndex = zIndex;
		window.emit('global:background-filter-activated', { 
			trigger: 'show', 
			zIndex, 
			hasZIndex: !!zIndex 
		});
	},

	/**
	 * Hide background filter
	 */
	hideBackgroundFilter() {
		const filter = window.getElement(this.config.selectors.filter);
		if (!filter) return;
		
		setTimeout(() => filter.style.removeProperty('z-index'), this.config.timing.filterHideDelay);
		filter.classList.remove(this.config.classes.active);
		window.emit('global:background-filter-deactivated', {
			trigger: 'hide',
			delay: this.config.timing.filterHideDelay
		});
	},

	/**
	 * Menu mobile affiche. En A/B test (MM-Header > menu_mobile_version = ab_test), plusieurs
	 * [data-menu-mobile] sont rendus et le CSS injecte par Intelligems n'en laisse qu'un en display.
	 * @returns {HTMLElement|null}
	 */
	getMobileMenu() {
		const menus = Array.from(window.getElements(this.config.selectors.menuMobile));
		return menus.find((el) => getComputedStyle(el).display !== 'none') || menus[0] || null;
	},

	/**
	 * Affiche/masque les overlays fixes (widget cookies Axeptio, chat Shopify)
	 * qui recouvriraient les liens de la bottom bar du menu mobile.
	 * @param {boolean} show - true pour restaurer, false pour masquer
	 */
	_toggleFixedOverlays(show) {
		['shopify-chat', 'axeptio_overlay'].forEach((id) => {
			const elem = document.getElementById(id);
			if (elem) elem.style.display = show ? '' : 'none';
		});
	},

	/**
	 * Initialize mobile submenu accessibility (replace inline onclick)
	 */
	initMobileSubmenuAccessibility() {
		// Submenu trigger buttons (show next)
		const submenuTriggers = window.getElements('[data-mobile-submenu-trigger]');
		submenuTriggers.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuTrigger;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowNext(targetSelector);
				btn.setAttribute('aria-expanded', 'true');

				// Focus sur le bouton retour du sous-menu : le declencheur passe en visibility hidden
				const panel = window.getElement(targetSelector);
				panel?.querySelector('[data-mobile-submenu-back]')?.focus();

				// Force Splide layout recompute once the panel is visible
				if (panel?.querySelector('[data-mobile-menu-slider]')) {
					requestAnimationFrame(() => {
						window.dispatchEvent(new Event('resize'));
					});
				}
			});
		});

		// Submenu back buttons (show prev)
		const submenuBackBtns = window.getElements('[data-mobile-submenu-back]');
		submenuBackBtns.forEach((btn) => {
			const targetSelector = btn.dataset.mobileSubmenuBack;

			btn.addEventListener('click', () => {
				window.mmMobileMenuShowPrev(targetSelector);
				// Find corresponding trigger and update aria-expanded
				const trigger = window.getElement(`[data-mobile-submenu-trigger="${targetSelector}"]`);
				if (trigger) {
					trigger.setAttribute('aria-expanded', 'false');
					// Focus rendu au declencheur (le sous-menu repasse en visibility hidden)
					trigger.focus();
				}
			});
		});

		window.log('info', 'NavigationManager', 'Mobile submenu accessibility initialized');
	},

	/**
	 * Handle window resize
	 */
	onResize() {
		this.initMegaMenus();
	}
};

/*********************************************************************************************
 * 📱 MOBILE MENU MODULE
 */
const MobileMenuManager = {
	// Reference to NavigationManager config
	config: NavigationManager.config,

	// Focus trap state
	focusTrap: {
		isActive: false,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null,
		triggerElement: null
	},

	/**
	 * Setup focus trap for mobile menu
	 */
	setupFocusTrap(mobileMenu) {
		// Store trigger element to restore focus on close
		this.focusTrap.triggerElement = document.activeElement;

		const focusableSelector = 'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

		if (mobileMenu.querySelectorAll(focusableSelector).length === 0) return;

		// Bornes recalculees a chaque Tab, sur les seuls elements atteignables : un sous-menu
		// ferme (visibility hidden) ne doit pas servir de premier/dernier, sinon le Tab sort
		// du menu au lieu de boucler (A11Y-05).
		const updateBounds = () => {
			const focusable = Array.from(mobileMenu.querySelectorAll(focusableSelector))
				// tabIndex >= 0 : exclut les boutons de pagination Splide inactifs (tabindex -1), jamais atteints au Tab
				.filter((el) => el.tabIndex >= 0 && !el.closest('[inert]') && (!el.checkVisibility || el.checkVisibility({ visibilityProperty: true })));
			this.focusTrap.firstFocusable = focusable[0] || null;
			this.focusTrap.lastFocusable = focusable[focusable.length - 1] || null;
		};
		updateBounds();

		// Handle Tab key to trap focus
		this.focusTrap.handleKeyDown = (e) => {
			if (e.key !== 'Tab') return;
			updateBounds();

			// Focus sur le wrapper (tabindex -1) ou hors du menu : le ramener sur une borne
			if (!mobileMenu.contains(document.activeElement) || document.activeElement === mobileMenu) {
				e.preventDefault();
				(e.shiftKey ? this.focusTrap.lastFocusable : this.focusTrap.firstFocusable)?.focus();
				return;
			}

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus the drawer container (tabindex="-1") rather than the first
		// interactive element. Focusing the first <summary>/link triggers a
		// visible :focus ring (barre bleue sur iOS) au chargement du menu ;
		// focus sur le wrapper entre dans le drawer sans surligner d'item.
		mobileMenu.focus();

		window.log('info', 'MobileMenuManager', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;

			// Restore focus to the element that triggered the menu
			if (this.focusTrap.triggerElement) {
				this.focusTrap.triggerElement.focus();
				this.focusTrap.triggerElement = null;
			}

			window.log('info', 'MobileMenuManager', 'Focus trap removed');
		}
	},

	/**
	 * Toggle mobile menu
	 */
	toggle() {
		if (!window.isMobile()) return;

		const header = window.getElement(this.config.selectors.header);
		const icon = window.getElement(this.config.selectors.headerBurger);
		const mobileMenu = NavigationManager.getMobileMenu();
		const html = document.documentElement;

		// Pas de condition sur [data-menu-sublinks] : un menu sans sous-ecran (V3) doit s'ouvrir aussi
		if (!header || !icon || !mobileMenu) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found');
			return;
		}

		const headerHeight = header.offsetHeight;
		const headerMenuTop = header.getBoundingClientRect().top;
		const topPosition = `${headerHeight + headerMenuTop}px`;
		const heightCalc = `calc(var(--doc-height) - ${topPosition})`;

		[header, icon, mobileMenu].forEach((el) => el.classList.toggle(this.config.classes.open));

		// Sous-menus (ecran 2) : top sur le haut du header (recouvre le
		// header pour cacher burger/logo mais on remontera .mm-header-right
		// par-dessus via CSS).
		const sublinksTop = `${Math.max(0, header.getBoundingClientRect().top)}px`;
		const sublinksHeight = `calc(var(--doc-height) - ${sublinksTop})`;

		Object.assign(mobileMenu.style, { top: topPosition, height: heightCalc });

		const sublinks = mobileMenu.querySelectorAll('[data-menu-sublinks]');
		sublinks.forEach((el) => Object.assign(el.style, { top: sublinksTop, height: sublinksHeight }));

		const isOpen = mobileMenu.classList.contains(this.config.classes.open);

		// Update burger aria-expanded (centralisé ici pour couvrir tous les cas d'appel)
		icon.setAttribute('aria-expanded', String(isOpen));

		if (isOpen) {
			html.classList.add(this.config.classes.stopScrolling);
			// Use z-index lower than mobile menu (menu is 10, filter should be 5)
			NavigationManager.showBackgroundFilter('var(--index-sm)');
			this.setupFocusTrap(mobileMenu);
			// Inert background content for screen readers (WCAG 2.4.3)
			document.querySelectorAll('main, footer').forEach(el => el.inert = true);
			// Masque les overlays fixes (widget cookies Axeptio, chat) qui
			// recouvriraient les liens de la bottom bar du menu (ex: Boutiques).
			NavigationManager._toggleFixedOverlays(false);

			// Recalcule top/height si l'announcement bar disparait (scroll)
			this._syncPosition = () => {
				const headerTop = header.getBoundingClientRect().top;
				const newTop = `${header.offsetHeight + headerTop}px`;
				const newHeight = `calc(var(--doc-height) - ${newTop})`;
				Object.assign(mobileMenu.style, { top: newTop, height: newHeight });

				const subTop = `${Math.max(0, headerTop)}px`;
				const subHeight = `calc(var(--doc-height) - ${subTop})`;
				mobileMenu.querySelectorAll('[data-menu-sublinks]').forEach((el) =>
					Object.assign(el.style, { top: subTop, height: subHeight })
				);
			};
			window.addEventListener('scroll', this._syncPosition, { passive: true });
		} else {
			html.classList.remove(this.config.classes.stopScrolling);
			NavigationManager.hideBackgroundFilter();
			this._resetSubmenus(mobileMenu);
			this.removeFocusTrap();
			document.querySelectorAll('[inert]').forEach(el => el.inert = false);
			NavigationManager._toggleFixedOverlays(true);

			if (this._syncPosition) {
				window.removeEventListener('scroll', this._syncPosition);
				this._syncPosition = null;
			}
		}

		window.emit('global:mobile-menu-toggled', { headerHeight, topPosition });
	},

	/**
	 * Close mobile menu
	 */
	close(keepFilterActive = false) {
		if (!window.isMobile()) return;

		const header = window.getElement(this.config.selectors.header);
		const icon = window.getElement(this.config.selectors.headerBurger);
		const mobileMenu = NavigationManager.getMobileMenu();
		const html = document.documentElement;

		if (!header || !icon || !mobileMenu) {
			window.log('warn', 'MobileMenuManager', 'Required elements not found for closing');
			return;
		}

		[header, icon, mobileMenu].forEach((el) => el.classList.remove(this.config.classes.open));
		html.classList.remove(this.config.classes.stopScrolling);

		// Update burger aria-expanded
		icon.setAttribute('aria-expanded', 'false');

		// Restore background content
		document.querySelectorAll('[inert]').forEach(el => el.inert = false);
		NavigationManager._toggleFixedOverlays(true);

		// Remove focus trap when menu closes
		this._resetSubmenus(mobileMenu);
		this.removeFocusTrap();

		if (this._syncPosition) {
			window.removeEventListener('scroll', this._syncPosition);
			this._syncPosition = null;
		}

		if (!keepFilterActive) NavigationManager.hideBackgroundFilter();
		window.emit('global:mobile-menu-closed', { keepFilterActive });
	},

	/**
	 * Referme les sous-menus ouverts a la fermeture du menu : sinon ils restent ouverts
	 * (et focusables) a la reouverture, avec un aria-expanded faux sur leur declencheur.
	 */
	_resetSubmenus(mobileMenu) {
		mobileMenu.querySelectorAll(`.${this.config.classes.mobileMenuSlided}`).forEach((el) => {
			el.classList.remove(this.config.classes.mobileMenuSlided);
		});
		mobileMenu.querySelectorAll('[data-mobile-submenu-trigger][aria-expanded="true"]').forEach((btn) => {
			btn.setAttribute('aria-expanded', 'false');
		});
	},

	/**
	 * Show next sub-menu
	 */
	showNext(elemToShowClass) {
		const element = window.getElement(elemToShowClass);
		if (element) {
			element.classList.add(this.config.classes.mobileMenuSlided);
			window.emit('global:mobile-submenu-next', { selector: elemToShowClass });
		}
	},

	/**
	 * Show previous sub-menu
	 */
	showPrev(elemToShowClass) {
		const element = window.getElement(elemToShowClass);
		if (element) {
			element.classList.remove(this.config.classes.mobileMenuSlided);
			window.emit('global:mobile-submenu-prev', { selector: elemToShowClass });
		}
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.NavigationManager = NavigationManager;
window.MobileMenuManager = MobileMenuManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
// Navigation functions
window.getMegaMenuPosition = () => NavigationManager.initMegaMenus();
window.mmMegaMenuShowProducts = (hide, show) => NavigationManager.showMegaMenuProducts(hide, show);

// Background filter functions
window.showBgFilter = (zIndex) => NavigationManager.showBackgroundFilter(zIndex);
window.hideBgFilter = () => NavigationManager.hideBackgroundFilter();

// Mobile menu functions
window.mmMobileMenu = () => MobileMenuManager.toggle();
window.mmCloseMobileMenu = (keep) => MobileMenuManager.close(keep);
window.mmMobileMenuShowNext = (elem) => MobileMenuManager.showNext(elem);
window.mmMobileMenuShowPrev = (elem) => MobileMenuManager.showPrev(elem);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	NavigationManager.init();
});

window.addEventListener('resize', window.debounce(() => {
	NavigationManager.onResize();
}, 150));